import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { DefaultButton, Icon, MessageBar, MessageBarType, PrimaryButton } from "@fluentui/react";
import { Tab, TabList } from "@fluentui/react-tabs";
import { t } from "i18next";
import { useBlocker, useNavigate } from "react-router-dom";

import CustomBreadCrumb from "../../components/CustomBreadCrumb";
import GroupBoard from "../../components/HangingProtocolV2/GroupBoard";
import ListViewPagination from "../../components/ListViewV2/ListViewPagination";
import SortPopupFilterSectionV2 from "../../components/ListViewV3/SortPopupFilterSectionV2";
import ConfirmModal from "../../components/Modal/ConfirmModal";
import { reorderByIds } from "../../helpers/hangingProtocol/editorOps";
import userContext from "../../context/users/UserContext";
import toastContext from "../../context/toast/ToastContext";
import { ACTIONS, AuthRoutes, TOAST_TYPE, USERTYPES } from "../../constants/Constants";
import * as hpv2 from "../../service/HPV2Service";
import "./hangingProtocolV2.css";

/**
 * Hanging protocol V2 groups.
 *
 * A group is the unit that owns ordered protocols, so this is the entry point
 * and both wizards hang off it.
 *
 * Nothing on this list writes as it is done. Reordering, activating and deleting
 * all edit a draft held here, and Save is what sends it — the same bargain the
 * wizards make with the document they edit, so the whole feature keeps one rule:
 * what is on screen is not what is stored until Save says so. A drag was the
 * reason. Order decides which protocol hangs, so a mis-drop on the shared list
 * changed what every radiologist got before the hand had left the mouse, with
 * nothing to undo it but dragging back.
 *
 * The wizards are the exception, deliberately: each is a screen of its own with
 * its own Save, and what it saves is a document body this list does not hold. So
 * adding or editing a group or a protocol still lands at once.
 *
 * A deletion is the one thing Save cannot take back, so it is the one thing Save
 * asks about, naming what is going. Until then a deleted row stays where it is,
 * struck through, with its delete button turned into an undo.
 *
 * That draft reaches the Advance Viewer in two different ways.
 *
 * On the shared set, saving and publishing are separate acts and each is its own
 * button: Save puts the draft in the database and into our own viewer, and the
 * Advance Viewer is given it when an admin presses Publish. Publish sends what is
 * stored, so an unsaved draft is neither part of it nor a reason to hold it back.
 *
 * On a radiologist's own set there is no Publish, because there is nothing to
 * decide: the protocols are theirs alone, so Save carries itself through to their
 * viewer. One publish for one Save rather than one per write — see
 * withDeferredSync in HPV2Service.
 *
 * The rows are GroupBoard rather than the shared list view: a group is one line
 * that expands to the protocols it owns, and both levels are drag-ordered, which
 * a table assembled from per-cell renderers cannot do. What is kept from the
 * shared screens is the frame around the rows — breadcrumb, search and top
 * buttons, pagination — so this reads as the same kind of screen as the rest.
 *
 * Order is semantic (first applicable protocol wins), so the list always shows
 * matching order and has no sort control.
 */

const PAGE_SIZE = 10;

/**
 * The two lists a radiologist has, as the tabs that show them.
 *
 * Their own protocols are a list to work in; the shared set is one to read. Tabs rather
 * than one list under the other: they are two orders, two authorities and two sets of
 * controls, and stacked they read as a single long list whose bottom half has quietly
 * stopped answering to the buttons at the top of the screen.
 *
 * An admin sees no tabs at all. The one list they have is the shared set.
 */
const SCOPE_TAB = { OWN: "own", SYSTEM: "system" };

/**
 * A tab's label, in the shape the app's other tab rows use: an icon, then the name,
 * brighter and heavier while it is the tab you are on.
 *
 * Written here rather than borrowed from the analytics screen's copy of it, whose type
 * and colours come from three page stylesheets that happen to be loaded beside it. The
 * values are the same ones; hangingProtocolV2.css states them for this screen.
 */
function ScopeTabLabel({ iconName, title, active }) {
  return (
    <span className="hpv2-scope-tab-label">
      <Icon iconName={iconName} className="hpv2-scope-tab-icon" />
      <span className={`hpv2-scope-tab-text ${active ? "is-active" : ""}`}>{title}</span>
    </span>
  );
}

const isOk = (response) => response?.status === 200 && response?.data?.result === "SUCCESS";

const sameOrder = (a, b) => a.length === b.length && a.every((id, index) => id === b[index]);

/**
 * What Save has to send: the difference between the groups the server last gave
 * us and the draft on screen.
 *
 * Exported for its own test. It is the part of this screen that can quietly do
 * the wrong thing — a permutation built over rows that are about to be deleted is
 * refused by the server, and an order compared against the wrong baseline either
 * sends a reorder that changes nothing or skips one that matters.
 *
 * A row marked for deletion is in no order and takes no activation with it: it is
 * leaving, and a group takes its protocols with it.
 */
export const changesToSave = (baseline, draft) => {
  const before = new Map(baseline.map((group) => [group.id, group]));

  const deletedGroups = draft.filter((group) => group.pendingDelete).map((group) => group.id);
  const going = new Set(deletedGroups);
  const surviving = draft.filter((group) => !group.pendingDelete);

  const deletedProtocols = surviving.flatMap((group) =>
    (group.protocols ?? [])
      .filter((protocol) => protocol.pendingDelete)
      .map((protocol) => protocol.id)
  );

  const activations = surviving
    .filter((group) => Boolean(group.active) !== Boolean(before.get(group.id)?.active))
    .map((group) => ({ id: group.id, active: Boolean(group.active) }));

  // Both orders are compared against the baseline with the same rows taken out of
  // it, so a deletion on its own is not mistaken for a reorder: the server closes
  // the gap itself.
  const groupOrder = surviving.map((group) => group.id);
  const groupOrderBefore = baseline.filter((group) => !going.has(group.id)).map((group) => group.id);

  const protocolOrders = surviving.flatMap((group) => {
    const kept = (group.protocols ?? [])
      .filter((protocol) => !protocol.pendingDelete)
      .map((protocol) => protocol.id);
    const keptIds = new Set(kept);
    const keptBefore = ((before.get(group.id) ?? {}).protocols ?? [])
      .map((protocol) => protocol.id)
      .filter((id) => keptIds.has(id));
    return sameOrder(kept, keptBefore) ? [] : [{ groupId: group.id, orderedIds: kept }];
  });

  return {
    activations,
    deletedProtocols,
    deletedGroups,
    groupOrder: sameOrder(groupOrder, groupOrderBefore) ? null : groupOrder,
    protocolOrders,
  };
};

/** Whether a plan has anything in it, which is also whether the draft is dirty. */
export const hasChanges = (plan) =>
  plan.activations.length > 0 ||
  plan.deletedProtocols.length > 0 ||
  plan.deletedGroups.length > 0 ||
  plan.groupOrder !== null ||
  plan.protocolOrders.length > 0;

/**
 * Where the wizards live, with the scope carried so a refresh keeps it.
 *
 * Three actions, three routes, because opening one to read is a different place
 * from opening it to change: the browser back button, a bookmark and a refresh all
 * have to land on the mode the link was for.
 */
const GROUP_ROUTE = {
  [ACTIONS.ADD]: AuthRoutes.HPV2_ADDGROUP,
  [ACTIONS.EDIT]: AuthRoutes.HPV2_EDITGROUP,
  [ACTIONS.VIEW]: AuthRoutes.HPV2_VIEWGROUP,
};

const PROTOCOL_ROUTE = {
  [ACTIONS.ADD]: AuthRoutes.HPV2_ADDPROTOCOL,
  [ACTIONS.EDIT]: AuthRoutes.HPV2_EDITPROTOCOL,
  [ACTIONS.VIEW]: AuthRoutes.HPV2_VIEWPROTOCOL,
};

export const groupFormUrl = (action, { id, scope, tab } = {}) => {
  const params = new URLSearchParams();
  if (id) params.set("id", String(id));
  if (scope) params.set("scope", scope);
  if (tab) params.set("tab", String(tab));
  const base = GROUP_ROUTE[action] ?? AuthRoutes.HPV2_EDITGROUP;
  return `${base}?${params.toString()}`;
};

export const protocolFormUrl = (action, { id, groupId, scope } = {}) => {
  const params = new URLSearchParams();
  if (id) params.set("id", String(id));
  if (groupId) params.set("groupId", String(groupId));
  if (scope) params.set("scope", scope);
  const base = PROTOCOL_ROUTE[action] ?? AuthRoutes.HPV2_EDITPROTOCOL;
  return `${base}?${params.toString()}`;
};

/**
 * The page numbers to offer: all of them while they fit, otherwise the ends and
 * a window around the current one. Mirrors what the shared table does, because
 * the pagination control it feeds is the shared one.
 */
const pageNumbers = (pageIndex, pageCount) => {
  if (pageCount <= 7) return Array.from({ length: pageCount }, (_unused, index) => index + 1);
  const current = pageIndex + 1;
  const window = [current - 1, current, current + 1].filter((page) => page > 1 && page < pageCount);
  const pages = [1, ...window, pageCount];
  return pages.flatMap((page, index) =>
    index > 0 && page - pages[index - 1] > 1 ? ["......", page] : [page]
  );
};

export default function HpV2GroupList() {
  const navigate = useNavigate();
  const { roles } = useContext(userContext);
  const { showToastv2 } = useContext(toastContext);

  const isAdmin = roles?.[0] === USERTYPES.ADMIN;
  // One scope per role, and no switch between them: an admin curates the shared system
  // protocols and has no personal set of their own, a radiologist has only their own.
  const scope = isAdmin ? hpv2.HP_SCOPE.SYSTEM : hpv2.HP_SCOPE.USER;

  // The groups as the server last gave them, and the draft the screen is editing. Both hold
  // the whole scope in matching order: the server sends all of it, paging and searching are
  // local, and a reorder needs the whole set anyway.
  //
  // Every edit below rebuilds the objects it touches rather than writing into them, which is
  // what lets Discard be `setGroups(baseline)` and the diff be a straight comparison.
  const [baseline, setBaseline] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [reloadToken, setReloadToken] = useState(0);

  const [confirmSave, setConfirmSave] = useState(false);
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const [confirmImport, setConfirmImport] = useState(null);
  const [confirmPublish, setConfirmPublish] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const importInputRef = useRef(null);

  // Whether the saved shared protocols differ from the ones the Advance Viewer is being
  // given. Null until the first fetch, and never fetched on the user scope, where a save
  // carries itself through.
  const [publishState, setPublishState] = useState(null);

  // The shared set, for a radiologist to read: an admin's protocols hang their studies
  // whether or not they have authored anything of their own, and nothing on this screen
  // used to say so. A radiologist who deletes their last group still gets a hang, and the
  // only honest answer to "where is that coming from" is the list on the other tab.
  //
  // Held apart from `groups` rather than merged into it, because nothing about it is
  // draftable: it has no activation to switch, no order this user may change and no row
  // they may delete, so it is not in the plan Save sends and must not be mistaken for it.
  const [systemGroups, setSystemGroups] = useState([]);
  // Whether the shared set answered at all, which is what puts the tabs on screen: a
  // server that refuses the read leaves the page exactly as it was before it had them.
  const [systemLoaded, setSystemLoaded] = useState(false);
  const [tab, setTab] = useState(SCOPE_TAB.OWN);

  const readingSystem = tab === SCOPE_TAB.SYSTEM;
  /** What the rows on screen belong to, and so what a wizard opened from one is for. */
  const boardScope = readingSystem ? hpv2.HP_SCOPE.SYSTEM : scope;

  const [pageIndex, setPageIndex] = useState(0);
  const [filterObj, setFilterObj] = useState({ filterInput: null, filterUpdate: 0 });
  const [sortObj, setSortObj] = useState({
    sortFieldText: "",
    sortFieldValue: "",
    sortDir: false,
    selected: false,
  });

  const refresh = useCallback(() => setReloadToken((token) => token + 1), []);

  const loadPublishState = useCallback(async () => {
    const response = await hpv2.fetchSystemPublishStatus();
    setPublishState(isOk(response) ? response.data.data : null);
  }, []);

  useEffect(() => {
    let stale = false;
    setLoading(true);
    (async () => {
      const response = await hpv2.fetchGroups(scope);
      if (stale) return;
      if (!isOk(response)) {
        setError(response?.data?.message ?? t("hanging-protocol.load-failed"));
        setBaseline([]);
        setGroups([]);
      } else {
        setError(null);
        const loaded = response.data.data ?? [];
        setBaseline(loaded);
        setGroups(loaded);
        // Ride along with the list load, and only for an admin: whether what they are
        // looking at has been published is the one thing this screen cannot tell them by
        // showing them the groups. A radiologist needs nothing here -- their saves publish
        // themselves, and the server rewrites their file as their next study opens.
        if (scope !== hpv2.HP_SCOPE.USER) loadPublishState();
      }
      setLoading(false);
    })();
    return () => {
      stale = true;
    };
  }, [scope, reloadToken, loadPublishState]);

  /**
   * The shared groups, read once, and only for a radiologist — an admin's own list is
   * the shared set already.
   *
   * Not tied to `reloadToken`: nothing on this screen can change these, so the only
   * thing a re-read would answer is what another admin did meanwhile, which the page
   * would be no more current about than a refresh.
   *
   * A refusal leaves the tab off the screen rather than putting an error over the page,
   * which also leaves an older server no worse than it was: this answers a question the
   * radiologist may not be asking, beside the list they came for and which loaded — so
   * it says nothing when it has nothing to say.
   */
  useEffect(() => {
    if (isAdmin) return undefined;
    let stale = false;
    (async () => {
      const response = await hpv2.fetchGroups(hpv2.HP_SCOPE.SYSTEM);
      if (stale) return;
      if (!isOk(response)) return;
      setSystemGroups(response.data.data ?? []);
      setSystemLoaded(true);
    })();
    return () => {
      stale = true;
    };
  }, [isAdmin]);

  /* ------------------------------------------------------------- the draft */

  const plan = useMemo(() => changesToSave(baseline, groups), [baseline, groups]);
  const dirty = hasChanges(plan);
  const deleting = plan.deletedGroups.length > 0 || plan.deletedProtocols.length > 0;

  /**
   * Both reorders are applied to the draft and go no further until Save. The server
   * takes a full permutation, which only the complete list held here can produce
   * since a page is a slice of it — but that is built at Save, from the draft, so a
   * run of drags costs one request rather than one each.
   */
  const reorderGroups = (activeId, overId) => {
    const order = reorderByIds(groups, activeId, overId);
    if (!order) return;
    setGroups(order.map((id) => groups.find((group) => group.id === id)));
  };

  const reorderProtocols = (groupId, activeId, overId) => {
    setGroups((current) =>
      current.map((group) => {
        if (group.id !== groupId) return group;
        const order = reorderByIds(group.protocols ?? [], activeId, overId);
        if (!order) return group;
        return {
          ...group,
          protocols: order.map((id) => group.protocols.find((protocol) => protocol.id === id)),
        };
      })
    );
  };

  const setActive = (group, active) =>
    setGroups((current) =>
      current.map((candidate) => (candidate.id === group.id ? { ...candidate, active } : candidate))
    );

  /**
   * Deleting is marking. The row keeps its place until Save, struck through and
   * offering the undo, because until then nothing has happened to it — and because
   * a group is read together with the protocols under it, which is what makes it
   * plain what a group taking its protocols with it actually costs.
   */
  const setGroupDeleted = (group, pendingDelete) =>
    setGroups((current) =>
      current.map((candidate) =>
        candidate.id === group.id ? { ...candidate, pendingDelete } : candidate
      )
    );

  const setProtocolDeleted = (group, protocol, pendingDelete) =>
    setGroups((current) =>
      current.map((candidate) =>
        candidate.id === group.id
          ? {
              ...candidate,
              protocols: (candidate.protocols ?? []).map((other) =>
                other.id === protocol.id ? { ...other, pendingDelete } : other
              ),
            }
          : candidate
      )
    );

  const discard = () => {
    setConfirmDiscard(false);
    setGroups(baseline);
  };

  /* ---------------------------------------------------------------- saving */

  /**
   * Send the plan, stopping at the first refusal.
   *
   * In the one order the server can accept: activations and deletions first, then the
   * permutations, which have to name every row that is left. Sequential rather than
   * parallel for the same reason — a reorder sent beside the delete it depends on
   * would name a group that is still there, or one that is already gone.
   */
  const applyPlan = async () => {
    for (const { id, active } of plan.activations) {
      const response = await hpv2.setGroupActive(id, active, scope);
      if (!isOk(response)) return { response, fallback: t("hanging-protocol.save-failed") };
    }
    for (const id of plan.deletedProtocols) {
      const response = await hpv2.deleteProtocol(id, scope);
      if (!isOk(response)) return { response, fallback: t("hanging-protocol.delete-failed") };
    }
    for (const id of plan.deletedGroups) {
      const response = await hpv2.deleteGroup(id, scope);
      if (!isOk(response)) return { response, fallback: t("hanging-protocol.delete-failed") };
    }
    if (plan.groupOrder) {
      const response = await hpv2.reorderGroups(plan.groupOrder, scope);
      if (!isOk(response)) return { response, fallback: t("hanging-protocol.reorder-failed") };
    }
    for (const { groupId, orderedIds } of plan.protocolOrders) {
      const response = await hpv2.reorderProtocols(groupId, orderedIds, scope);
      if (!isOk(response)) return { response, fallback: t("hanging-protocol.reorder-failed") };
    }
    return null;
  };

  const commit = async () => {
    setConfirmSave(false);
    if (saving || !dirty) return;
    setSaving(true);
    // One publish for the whole batch on the user scope, where a save is also a delivery.
    const failure = await hpv2.withDeferredSync(applyPlan);
    setSaving(false);

    if (failure) {
      showToastv2(TOAST_TYPE.ERROR, failure.response?.data?.message ?? failure.fallback);
    } else {
      // Settle the draft rather than wait for the refetch: the rows that were leaving are
      // gone, and the screen must not go on offering to save what it has just saved.
      const settled = groups
        .filter((group) => !group.pendingDelete)
        .map((group) => ({
          ...group,
          protocols: (group.protocols ?? []).filter((protocol) => !protocol.pendingDelete),
        }));
      setBaseline(settled);
      setGroups(settled);
      showToastv2(TOAST_TYPE.SUCCESS, t("hanging-protocol.changes-saved"));
    }
    // Either way, re-read. A batch that stopped part way through leaves the server holding
    // some of the draft, and that is the state to show.
    refresh();
  };

  /** Deletions are what Save cannot take back, so they are what it asks about. */
  const save = () => {
    if (saving || !dirty) return;
    if (deleting) setConfirmSave(true);
    else commit();
  };

  /** Named, not counted: what makes a deletion answerable is knowing which rows it takes. */
  const deletionSummary = () => {
    const groupName = (group) => group.name || t("hanging-protocol.group");
    const lines = groups
      .filter((group) => group.pendingDelete)
      .map((group) => t("hanging-protocol.delete-summary-group", { name: groupName(group) }));

    groups
      .filter((group) => !group.pendingDelete)
      .forEach((group) =>
        (group.protocols ?? [])
          .filter((protocol) => protocol.pendingDelete)
          .forEach((protocol) =>
            lines.push(
              t("hanging-protocol.delete-summary-protocol", {
                name: protocol.name || t("hanging-protocol.new-protocol"),
                group: groupName(group),
              })
            )
          )
      );

    return lines.join("\n");
  };

  /* ----------------------------------------------------- leaving the draft */

  /**
   * A draft only exists on this screen, so leaving it asks first — including by the
   * side nav, which this component does not own. The router's blocker is what reaches
   * that; a guard on our own controls would cover only the ones on this page.
   */
  const blocker = useBlocker(
    useCallback(
      ({ currentLocation, nextLocation }) =>
        dirty && currentLocation.pathname !== nextLocation.pathname,
      [dirty]
    )
  );

  useEffect(() => {
    if (!dirty) return undefined;
    // Reload and tab close are the browser's to ask about, and it only asks when the
    // event is cancelled.
    const onBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [dirty]);

  /* ----------------------------------------------------- search and paging */

  const search = (filterObj.filterInput ?? "").trim().toLowerCase();

  /** The list the tab puts on screen: their own to work in, or the shared set to read. */
  const listed = readingSystem ? systemGroups : groups;

  const matching = useMemo(
    () => listed.filter((group) => !search || (group.name ?? "").toLowerCase().includes(search)),
    [listed, search]
  );

  const pageCount = Math.max(1, Math.ceil(matching.length / PAGE_SIZE));
  const page = Math.min(pageIndex, pageCount - 1);

  /**
   * The number on a row is the group's matching position in the whole scope, not its
   * place on this page: it is the order the server matches in. A row on its way out
   * has no position — it is in no order that Save will send — so it is numbered
   * around, rather than renumbering everything below it twice.
   */
  const positions = useMemo(() => {
    const numbers = new Map();
    let position = 0;
    groups.forEach((group) => {
      if (group.pendingDelete) return;
      position += 1;
      numbers.set(group.id, position);
    });
    return numbers;
  }, [groups]);

  /**
   * Where a shared group sits in that same order: after every group of this user's own.
   * The server assembles a user's groups first and the system ones behind them, so a
   * shared tab numbered from 1 would claim a place in the matching order it has not got.
   *
   * Numbered before the search is applied, as their own rows are: a row's number is its
   * position in the whole order, not on the filtered page.
   */
  const systemPositions = useMemo(() => {
    const numbers = new Map();
    systemGroups.forEach((group, index) => numbers.set(group.id, positions.size + index + 1));
    return numbers;
  }, [systemGroups, positions]);

  const rows = useMemo(() => {
    const numbers = readingSystem ? systemPositions : positions;
    return matching
      .slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE)
      .map((group) => ({ ...group, position: numbers.get(group.id) ?? null }));
  }, [matching, positions, systemPositions, readingSystem, page]);

  /** The shared pagination control is built for react-table; this is the part of it it uses. */
  const paginationTable = useMemo(
    () => ({
      getCanPreviousPage: () => page > 0,
      getCanNextPage: () => page + 1 < pageCount,
      previousPage: () => setPageIndex(Math.max(0, page - 1)),
      nextPage: () => setPageIndex(Math.min(pageCount - 1, page + 1)),
      setPageIndex: (index) => setPageIndex(index),
      getPageCount: () => pageCount,
      getState: () => ({ pagination: { pageIndex: page, pageSize: PAGE_SIZE } }),
    }),
    [page, pageCount]
  );

  /* -------------------------------------------------------------- publish */

  /**
   * Hand the shared protocols to the radiologists' Advance Viewer.
   *
   * The only act on this screen that changes what anyone else is reading from, which is why
   * it is the only one besides a deletion that asks first. It writes one file, which every
   * radiologist without protocols of their own reads when their next viewer session starts;
   * our own viewer has had these protocols since they were saved.
   *
   * What it sends is what is stored, not what is on screen: an unsaved draft is no part of
   * it, and no reason to hold it back either.
   */
  const doPublish = async () => {
    setConfirmPublish(false);
    setPublishing(true);
    const response = await hpv2.publishSystem();
    setPublishing(false);
    if (isOk(response)) {
      showToastv2(TOAST_TYPE.SUCCESS, t("hanging-protocol.published"));
      loadPublishState();
    } else {
      showToastv2(TOAST_TYPE.ERROR, response?.data?.message ?? t("hanging-protocol.publish-failed"));
    }
  };

  /* ------------------------------------------------------------- document */

  const exportDocument = async () => {
    const response = await hpv2.exportDocument(scope);
    if (!isOk(response)) {
      showToastv2(TOAST_TYPE.ERROR, response?.data?.message ?? t("hanging-protocol.export-failed"));
      return;
    }
    const blob = new Blob([JSON.stringify(response.data.data, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `hangingProtocolsV2-${scope.toLowerCase()}.json`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const pickImportFile = () => importInputRef.current?.click();

  const onImportFileChosen = async (event) => {
    const file = event.target.files?.[0];
    // Reset so choosing the same file twice still fires a change.
    event.target.value = "";
    if (!file) return;
    try {
      setConfirmImport(JSON.parse(await file.text()));
    } catch {
      showToastv2(TOAST_TYPE.ERROR, t("hanging-protocol.import-invalid"));
    }
  };

  const doImport = async () => {
    const doc = confirmImport;
    setConfirmImport(null);
    if (!doc) return;
    const response = await hpv2.importDocument(doc, scope);
    if (isOk(response)) {
      showToastv2(TOAST_TYPE.SUCCESS, t("hanging-protocol.imported"));
      setPageIndex(0);
      refresh();
    } else {
      showToastv2(TOAST_TYPE.ERROR, response?.data?.message ?? t("hanging-protocol.import-failed"));
    }
  };

  /* -------------------------------------------------------------- render */

  /**
   * What an empty board says, which is not the same question on the two tabs. Their own
   * list is one to start, and while it is loading it says so. The shared set is somebody
   * else's and may simply be empty — or hold nothing by the name that was searched for.
   * Never "add one to start" there: there is nothing on that tab for this user to add.
   */
  const emptyBoardMessage = readingSystem
    ? t(search ? "hanging-protocol.system-none-match" : "hanging-protocol.system-none")
    : (loading ? t("hanging-protocol.loading") : undefined);

  // Cancel and Save are not here: they live in the footer bar at the foot of the page,
  // with the rest of the app's commit points. What is left up here opens something —
  // a wizard, a file, the Advance Viewer — rather than committing the draft.
  const ownTabButtons = [
    // Admin only. Saving is not the last step on the shared set, and the button is where an
    // admin learns that.
    ...(isAdmin
      ? [
          {
            key: "publish",
            button: (
              <DefaultButton
                id="publish-hpv2-system"
                className="hpv2-list-top-button"
                text={t("hanging-protocol.publish")}
                disabled={publishing || !publishState?.hasUnpublishedChanges}
                onClick={() => setConfirmPublish(true)}
              />
            ),
          },
        ]
      : []),
    {
      key: "export",
      button: (
        <DefaultButton
          className="hpv2-list-top-button"
          text={t("hanging-protocol.export")}
          onClick={exportDocument}
        />
      ),
    },
    {
      key: "import",
      button: (
        <>
          {/* Closed while a draft is open: an import replaces the whole scope, so it would
              throw the draft away without ever having asked about it. */}
          <DefaultButton
            className="hpv2-list-top-button"
            text={t("hanging-protocol.import")}
            disabled={dirty || saving}
            title={dirty ? t("hanging-protocol.save-first") : undefined}
            onClick={pickImportFile}
          />
          <input
            ref={importInputRef}
            type="file"
            accept="application/json,.json"
            className="hpv2-list-file-input"
            aria-label={t("hanging-protocol.import")}
            onChange={onImportFileChosen}
          />
        </>
      ),
    },
    {
      key: "add",
      button: (
        <div className="add-button-list-view">
          <PrimaryButton
            id="add-hpv2-group"
            text={t("hanging-protocol.add-group-button")}
            className="common-default-active-button list-view-top-buttons-styles"
            onClick={() => navigate(groupFormUrl(ACTIONS.ADD, { scope }))}
          />
        </div>
      ),
    },
  ];

  /**
   * None of them on the shared tab. Every button up there acts on the scope this user may
   * write — add a group to it, replace it from a file, take it away as one — and on that
   * tab they may write none of it. A row of controls that quietly means the other list is
   * worse than no row at all.
   */
  const buttonsTopBar = readingSystem ? [] : ownTabButtons;

  return (
    <>
      {confirmSave && (
        <ConfirmModal
          showConfirmModal
          title={t("confirm-message.confirm")}
          message={t("hanging-protocol.save-deletes-confirm", { items: deletionSummary() })}
          confirmButtonText={t("hanging-protocol.save")}
          handleConfirm={commit}
          handleNotConfirm={() => setConfirmSave(false)}
        />
      )}

      {confirmDiscard && (
        <ConfirmModal
          showConfirmModal
          title={t("hanging-protocol.unsaved-title")}
          message={t("hanging-protocol.discard-confirm")}
          confirmButtonText={t("hanging-protocol.discard")}
          handleConfirm={discard}
          handleNotConfirm={() => setConfirmDiscard(false)}
        />
      )}

      {blocker?.state === "blocked" && (
        <ConfirmModal
          showConfirmModal
          title={t("hanging-protocol.unsaved-title")}
          message={t("hanging-protocol.unsaved-body")}
          confirmButtonText={t("hanging-protocol.discard")}
          handleConfirm={() => blocker.proceed()}
          handleNotConfirm={() => blocker.reset()}
        />
      )}

      {confirmImport && (
        <ConfirmModal
          showConfirmModal
          title={t("confirm-message.confirm")}
          message={t("hanging-protocol.import-confirm")}
          confirmButtonText={t("confirm-message.yes")}
          handleConfirm={doImport}
          handleNotConfirm={() => setConfirmImport(null)}
        />
      )}

      {confirmPublish && (
        <ConfirmModal
          showConfirmModal
          title={t("confirm-message.confirm")}
          message={t("hanging-protocol.publish-confirm")}
          confirmButtonText={t("confirm-message.yes")}
          handleConfirm={doPublish}
          handleNotConfirm={() => setConfirmPublish(false)}
        />
      )}

      <div className="main-container-body-list-view react-scroll">
        {/* Two lists, one screen, as tabs beside the page name — where the analytics and
            troubleshooting screens put theirs. Only a radiologist has both, and only once
            the shared read has answered: see the effect above.

            Everything under the tabs belongs to the tab, the search row with its Add,
            Import and Export included. That is why they are up here rather than under
            that row, which is where the study list keeps its own. */}
        <div className={`breadCrumb ${systemLoaded ? "hpv2-breadcrumb-tabs" : ""}`}>
          <CustomBreadCrumb
            items={[{ text: t("hanging-protocol.list"), key: "hanging-protocol.list" }]}
          />

          {systemLoaded && (
            <TabList
              className="hpv2-scope-tabs"
              data-testid="hpv2-scope-tabs"
              selectedValue={tab}
              onTabSelect={(_event, data) => {
                setTab(data.value);
                // The pages belong to the list, not to the screen.
                setPageIndex(0);
              }}
            >
              <Tab className="hpv2-scope-tab" id="hpv2-scope-tab-own" value={SCOPE_TAB.OWN}>
                <ScopeTabLabel
                  iconName="Contact"
                  title={t("hanging-protocol.tab-own")}
                  active={!readingSystem}
                />
              </Tab>
              <Tab className="hpv2-scope-tab" id="hpv2-scope-tab-system" value={SCOPE_TAB.SYSTEM}>
                <ScopeTabLabel
                  iconName="People"
                  title={t("hanging-protocol.tab-system")}
                  active={readingSystem}
                />
              </Tab>
            </TabList>
          )}
        </div>

        <SortPopupFilterSectionV2
          sortObj={sortObj}
          setSortObj={setSortObj}
          filterObj={filterObj}
          setFilterObj={setFilterObj}
          buttonsTopBar={buttonsTopBar}
          placeholder={t("hanging-protocol.name")}
          showSort={false}
          handleApplyClick={(value) => {
            setPageIndex(0);
            setFilterObj((current) => ({
              ...current,
              filterInput: value == null ? null : value.trim(),
              filterUpdate: current.filterUpdate + 1,
            }));
          }}
        />

        {/* What the shared tab is, said once at the top of it: where these came from, that
            they hang after the reader's own, and that nothing here is theirs to change.
            It heads the tab rather than sitting on the rows, which have their own work to
            do — and it is the answer this screen exists to give. */}
        {readingSystem && (
          <MessageBar messageBarType={MessageBarType.info}>
            {t("hanging-protocol.system-note")}
          </MessageBar>
        )}
        {error && (
          <MessageBar messageBarType={MessageBarType.error} onDismiss={() => setError(null)}>
            {error}
          </MessageBar>
        )}
        {/* Not dismissible: it is the answer to "why has nothing changed in the viewer", and
            it goes when the thing it reports goes. */}
        {dirty && (
          <MessageBar messageBarType={MessageBarType.warning}>
            {t("hanging-protocol.unsaved-list")}
          </MessageBar>
        )}
        {/* Worded around what radiologists have rather than what the admin has done: these
            groups are saved, so nothing on the list itself says the Advance Viewer has not
            been given them. It carries no button of its own -- the one in the top bar is
            where Publish always is, whether or not it is needed yet. */}
        {publishState?.hasUnpublishedChanges && (
          <MessageBar messageBarType={MessageBarType.warning}>
            {t("hanging-protocol.unpublished-changes")}
          </MessageBar>
        )}

        {/* One board, showing whichever list the tab selected.

            On the shared tab it is the same board told it may not be written to: the rows
            still open to read and the state button still says which groups are live, but
            there is no grip, no Add and no Delete. The wizards it opens hold that same
            line — see lockedByScope in both of them — and so does the server, which
            gives a radiologist the shared scope to read and refuses every write to it. */}
        <GroupBoard
          groups={rows}
          readOnly={readingSystem}
          emptyMessage={emptyBoardMessage}
          /* A row opens what it is, to read. Changing it is a decision of its own,
             taken on the wizard behind its Edit button — the same order every other
             list in the app puts the two acts in. */
          onOpenGroup={(group) =>
            navigate(groupFormUrl(ACTIONS.VIEW, { id: group.id, scope: boardScope }))
          }
          onOpenProtocol={(group, protocol) =>
            navigate(
              protocolFormUrl(ACTIONS.VIEW, {
                id: protocol.id,
                groupId: group.id,
                scope: boardScope,
              })
            )
          }
          onAddProtocol={(group) =>
            navigate(protocolFormUrl(ACTIONS.ADD, { groupId: group.id, scope }))
          }
          onDeleteProtocol={(group, protocol) => setProtocolDeleted(group, protocol, true)}
          onRestoreProtocol={(group, protocol) => setProtocolDeleted(group, protocol, false)}
          onReorderGroups={reorderGroups}
          onReorderProtocols={reorderProtocols}
          onToggleActive={setActive}
          onDelete={(group) => setGroupDeleted(group, true)}
          onRestore={(group) => setGroupDeleted(group, false)}
        />

        {/* One bar closes the page: which page you are on at one end, what to do with
            the draft at the other. The commit point is where it is on every form in the
            app, and the pager rides along with it rather than taking a row of its own —
            there are rarely enough groups for a second page to be worth a row. Both
            buttons stay in place rather than appearing with the changes, and go flat
            when there are none — a control that comes and goes is one nobody learns is
            there, and this pair is the whole reason the screen no longer saves under
            the hand. */}
        <div className="hpv2-list-footer-actions">
          {/* In the bar whether or not it has a pager in it: it is what holds the
              buttons at the far end, so a single page of groups must not slide them
              across. */}
          <div className="hpv2-list-footer-pagination">
            {matching.length > 0 && (
              <ListViewPagination
                table={paginationTable}
                arrayPageIndex={pageNumbers(page, pageCount)}
                pageIndex={page}
                pageSize={PAGE_SIZE}
                pagination={{ pageIndex: page, pageSize: PAGE_SIZE }}
              />
            )}
          </div>

          {/* The pair belongs to the list it commits, so the shared tab does without it
              rather than showing a Save that would quietly send the draft held by the
              other tab. Which is also why the unsaved-changes bar above stays up on both:
              a draft is never hidden, only the button that sends it. */}
          {!readingSystem && (
            <div className="hpv2-list-footer-buttons">
              <DefaultButton
                id="discard-hpv2-changes"
                text={t("button.cancel")}
                disabled={!dirty || saving}
                onClick={() => setConfirmDiscard(true)}
              />
              <PrimaryButton
                id="save-hpv2-changes"
                text={t("hanging-protocol.save")}
                disabled={!dirty || saving}
                onClick={save}
              />
            </div>
          )}
        </div>
      </div>
    </>
  );
}
